import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import Login from './Login';
import Register from './Register';
import VerifyOtp from './VerifyOtp';
import Homepage from './Homepage';
import ProfileCreation from './ProfileCreation';
import AnnouncementPage from './AnnouncementPage';
import AnnouncementDetail from './AnnouncementDetail';
import ManageUsers from './ManageUsers';
import MissingReportsHub from './MissingReportsHub';
import MissingReportDetail from './MissingReportDetail';
import MissingReportInteractions from './MissingReportInteractions';
import IncidentReportDetail from './IncidentReportDetail';
import IncidentMap from './IncidentMap';
import EvacuationMap from './EvacuationMap';
import Dashboard from './Dashboard';
import Reports from './Reports';
import ApprovedShelters from './ApprovedShelters';
import PendingShelters from './PendingShelters';
import PendingShelterDetail from './PendingShelterDetail';
import EvacuationShelters from './EvacuationShelters';
import EvacuationMonitoring from './EvacuationMonitoring';
import TempShelterMonitoring from './TempShelterMonitoring';
import SOSBeaconPage from './SOSBeaconPage';
import SOSDetail from './SOSDetail';
import IDVerification from './IDVerification';
import IDVerificationDetails from './IDVerificationDetails';
import ResponderApplications from './ResponderApplications';
import ApprovedResponders from './ApprovedResponders';
import ResponderIncidentTracking from './ResponderIncidentTracking';
import AvailableIncidents from './AvailableIncidents';
import ResponderMap from './ResponderMap';
import Settings from './Settings';
import { NotificationProvider, useNotification } from './NotificationContext';
import { supabase } from './supabaseClient';
import { startAutoArchiveScheduler } from './utils/autoArchive';

function GlobalSOSListener() {
  const { showBanner } = useNotification();
  const { user } = useAuth();

  useEffect(() => {
    let channel;
    let isMounted = true;
    async function setup() {
      if (!user) return;
      // Fetch user profile to get role
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();
      if (!profile || (profile.role !== 'admin' && profile.role !== 'super admin')) return;
      channel = supabase
        .channel('public:sos_beacons_global')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sos_beacons' }, (payload) => {
          if (isMounted) {
            showBanner('New SOS Activated! Check the SOS Beacons page.');
          }
        })
        .subscribe();
    }
    setup();
    return () => {
      isMounted = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, [user, showBanner]);
  return null;
}

function AutoArchiveManager() {
  const { user } = useAuth();

  useEffect(() => {
    let stopScheduler;
    let isMounted = true;

    async function setup() {
      if (!user) return;
      
      try {
        // Fetch user profile to get role
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('user_id', user.id)
          .single();
        
        // Only start auto-archiving for admin users
        if (!profile || (profile.role !== 'admin' && profile.role !== 'super admin')) return;
        
        if (isMounted) {
          console.log('Starting auto-archive manager for admin user');
          // Start the auto-archive scheduler
          stopScheduler = startAutoArchiveScheduler();
        }
      } catch (error) {
        console.error('Error setting up auto-archive manager:', error);
      }
    }

    setup();

    return () => {
      isMounted = false;
      if (stopScheduler) {
        stopScheduler();
      }
    };
  }, [user]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <GlobalSOSListener />
          <AutoArchiveManager />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Homepage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            
            {/* Protected Routes */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfileCreation />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/incident-reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/announcements" element={
              <ProtectedRoute>
                <AnnouncementPage />
              </ProtectedRoute>
            } />
            <Route path="/announcements/:id" element={
              <ProtectedRoute>
                <AnnouncementDetail />
              </ProtectedRoute>
            } />
            <Route path="/manage-users" element={
              <ProtectedRoute>
                <ManageUsers />
              </ProtectedRoute>
            } />
            <Route path="/missing-reports" element={
              <ProtectedRoute>
                <MissingReportsHub />
              </ProtectedRoute>
            } />
            <Route path="/missing-reports/:id" element={
              <ProtectedRoute>
                <MissingReportDetail />
              </ProtectedRoute>
            } />
            <Route path="/missing-report-interactions" element={
              <ProtectedRoute>
                <MissingReportInteractions />
              </ProtectedRoute>
            } />
            <Route path="/temporary-shelters" element={
              <ProtectedRoute>
                <ApprovedShelters />
              </ProtectedRoute>
            } />
            <Route path="/temporary-shelters/approved" element={
              <ProtectedRoute>
                <ApprovedShelters />
              </ProtectedRoute>
            } />
            <Route path="/temporary-shelters/pending" element={
              <ProtectedRoute>
                <PendingShelters />
              </ProtectedRoute>
            } />
            <Route path="/incident-reports/:id" element={
              <ProtectedRoute>
                <IncidentReportDetail />
              </ProtectedRoute>
            } />
            <Route path="/maps/incident" element={
              <ProtectedRoute>
                <IncidentMap />
              </ProtectedRoute>
            } />
            <Route path="/maps/evacuation" element={
              <ProtectedRoute>
                <EvacuationMap />
              </ProtectedRoute>
            } />
            <Route path="/evacuation-shelters" element={
              <ProtectedRoute>
                <EvacuationShelters />
              </ProtectedRoute>
            } />
            <Route path="/evacuation-monitoring/:id" element={
              <ProtectedRoute>
                <EvacuationMonitoring />
              </ProtectedRoute>
            } />
            <Route path="/temp-shelter-monitoring/:id" element={
              <ProtectedRoute>
                <TempShelterMonitoring />
              </ProtectedRoute>
            } />
            <Route path="/sos-beacons" element={
              <ProtectedRoute>
                <SOSBeaconPage />
              </ProtectedRoute>
            } />
            <Route path="/sos-beacons/:id" element={
              <ProtectedRoute>
                <SOSDetail />
              </ProtectedRoute>
            } />
            <Route path="/pending-shelter-detail/:id" element={
              <ProtectedRoute>
                <PendingShelterDetail />
              </ProtectedRoute>
            } />
            <Route path="/id-verification" element={
              <ProtectedRoute>
                <IDVerification />
              </ProtectedRoute>
            } />
            <Route path="/id-verification/:id" element={
              <ProtectedRoute>
                <IDVerificationDetails />
              </ProtectedRoute>
            } />
            <Route path="/responder-applications" element={
              <ProtectedRoute>
                <ResponderApplications />
              </ProtectedRoute>
            } />
            <Route path="/approved-responders" element={
              <ProtectedRoute>
                <ApprovedResponders />
              </ProtectedRoute>
            } />
            <Route path="/responder-incident-tracking" element={
              <ProtectedRoute>
                <ResponderIncidentTracking />
              </ProtectedRoute>
            } />
            {/* Replace Available Incidents with Responder Map */}
            <Route path="/responder-map" element={
              <ProtectedRoute>
                <ResponderMap />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;